import {
  MEDIA_TYPE,
  type Participant,
  useConfigStore,
  useRaisedHandTimestamp,
  useTrackMuted,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import { memo, useMemo } from 'react';
import Icon from '../icon/Icon.web';
import { Wave } from '../wave-animated/Wave';
import './label.web.css';

interface LabelProps {
  participant: Participant;
  className?: string;
}

const ICON_FILLS = {
  secondary: 'var(--cometchat-calls-icon-color-secondary)',
  primary: 'var(--cometchat-calls-static-black)',
  success: 'var(--cometchat-calls-success-color)',
} as const;

const LabelIcon = memo(
  ({
    type,
    muted,
    raisedHandTimestamp,
    pid,
  }: {
    type: Participant['type'];
    muted?: boolean;
    raisedHandTimestamp?: number;
    pid: string;
  }) => {
    if (type === 'screen-share') {
      return <Icon name="screen-share" size={16} fill={ICON_FILLS.secondary} />;
    }
    if (raisedHandTimestamp) {
      return (
        <Icon name="raise-hand-fill" size={16} fill={ICON_FILLS.primary} />
      );
    }
    if (muted) {
      return <Icon name="mic-off-fill" size={16} fill={ICON_FILLS.secondary} />;
    }
    return <Wave pid={pid} />;
  },
);

const Label = ({ participant, className }: LabelProps) => {
  const { name, isLocal, type, pid } = participant;
  const muted = useTrackMuted(MEDIA_TYPE.AUDIO, pid);
  const raisedHandTimestamp = useRaisedHandTimestamp(pid, type);
  const enableCompanionMode = useConfigStore(
    state => state.enableCompanionMode,
  );

  const isRaisedHand = Boolean(raisedHandTimestamp) && type === 'human';

  const labelClassName = useMemo(
    () =>
      clsx(
        'cometchat-calls-tile-label',
        isRaisedHand && 'cometchat-calls-tile-label--raised-hand',
        className,
      ),
    [isRaisedHand, className],
  );

  const nameClassName = useMemo(() => {
    return `cometchat-calls-tile-label-name ${isRaisedHand ? 'cometchat-calls-tile-label-name--raised-hand' : ''}`;
  }, [isRaisedHand]);

  return (
    <div className={labelClassName}>
      <LabelIcon
        type={type}
        muted={isLocal && enableCompanionMode ? true : muted}
        raisedHandTimestamp={raisedHandTimestamp}
        pid={pid}
      />
      <span className={nameClassName}>
        {name} {isLocal && '(You)'}
      </span>
    </div>
  );
};

export default Label;
